(function () {
  class QuickAddModal extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound === 'true') return;
      this.dataset.bound = 'true';
      this.cache = new Map();
      this.requestId = 0;
      this.previouslyFocusedElement = null;
      this.product = null;
      this.currentVariant = null;

      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      document.addEventListener('click', this.handleDocumentClick);
      document.addEventListener('keydown', this.handleKeydown);

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-quick-add-close]')) this.close();
      });

      this.addEventListener('change', (event) => {
        const input = event.target.closest('[data-quick-add-option]');
        if (input) this.handleOptionChange(input);
      });

      this.querySelector('[data-quick-add-form]')?.addEventListener('submit', (event) => {
        this.handleSubmit(event);
      });

    }

    disconnectedCallback() {
      document.removeEventListener('click', this.handleDocumentClick);
      document.removeEventListener('keydown', this.handleKeydown);
    }

    handleDocumentClick(event) {
      const trigger = event.target.closest('[data-quick-add-open]');
      if (!trigger || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.openProduct(trigger);
    }

    productEndpoint(productUrl) {
      const url = new URL(productUrl, window.location.origin);
      url.pathname = `${url.pathname.replace(/\/$/, '')}.js`;
      url.search = '';
      return url.toString();
    }

    async fetchProduct(productUrl) {
      const endpoint = this.productEndpoint(productUrl);
      if (!this.cache.has(endpoint)) {
        this.cache.set(endpoint, fetch(endpoint, { headers: { Accept: 'application/json' } }).then(async (response) => {
          const data = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(data.description || data.message || this.strings().loadError);
          return data;
        }).catch((error) => {
          this.cache.delete(endpoint);
          throw error;
        }));
      }
      return this.cache.get(endpoint);
    }

    async openProduct(trigger) {
      const productUrl = trigger.dataset.productUrl || trigger.getAttribute('href');
      if (!productUrl) return;

      const requestId = ++this.requestId;
      this.previouslyFocusedElement = trigger;
      this.setLoading(true);
      this.open();

      try {
        const product = await this.fetchProduct(productUrl);
        if (requestId !== this.requestId || !this.classList.contains('is-open')) return;
        this.renderProduct(product, trigger.dataset.quickAddBadge || '');
      } catch (error) {
        if (requestId !== this.requestId) return;
        this.showLoadError(error.message || this.strings().loadError);
      }
    }

    open() {
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      window.requestAnimationFrame(() => {
        this.querySelector('[data-quick-add-close]')?.focus({ preventScroll: true });
      });
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.requestId += 1;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (this.previouslyFocusedElement && document.contains(this.previouslyFocusedElement)) {
        this.previouslyFocusedElement.focus({ preventScroll: true });
      }
      this.previouslyFocusedElement = null;
    }

    setLoading(isLoading) {
      const loading = this.querySelector('[data-quick-add-loading]');
      const content = this.querySelector('[data-quick-add-content]');
      const spinner = this.querySelector('[data-quick-add-spinner]');
      const loadingText = this.querySelector('[data-quick-add-loading-text]');
      if (loading) loading.hidden = !isLoading;
      if (content) content.hidden = isLoading;
      if (spinner) spinner.hidden = false;
      if (loadingText) {
        loadingText.classList.remove('quick-add-modal__load-error');
        loadingText.textContent = this.strings().loading || '';
      }
      this.showError('');
    }

    showLoadError(message) {
      const loading = this.querySelector('[data-quick-add-loading]');
      const spinner = this.querySelector('[data-quick-add-spinner]');
      const loadingText = this.querySelector('[data-quick-add-loading-text]');
      if (spinner) spinner.hidden = true;
      if (loadingText) {
        loadingText.classList.add('quick-add-modal__load-error');
        loadingText.textContent = message;
      }
      loading.hidden = false;
    }

    strings() {
      return window.theme?.quickAdd?.strings || {};
    }

    formatMoney(cents) {
      if (window.theme?.formatMoney) return window.theme.formatMoney(cents);
      return `${(Number(cents || 0) / 100).toFixed(2).replace('.', ',')} €`;
    }

    normalizeImage(image) {
      const source = typeof image === 'string' ? image : image?.src || image?.url || '';
      return source.startsWith('//') ? `${window.location.protocol}${source}` : source;
    }

    renderProduct(product, badgeText) {
      this.product = product;
      this.currentVariant = product.variants.find((variant) => variant.available) || product.variants[0] || null;

      const title = this.querySelector('[data-quick-add-title]');
      const badge = this.querySelector('[data-quick-add-badge]');
      const quantity = this.querySelector('.quantity__input');
      if (title) title.textContent = product.title || '';
      if (badge) {
        badge.textContent = badgeText || this.strings().bestSeller || '';
        badge.hidden = !badge.textContent;
      }
      if (quantity) quantity.value = 1;

      this.renderOptions(product);
      this.applySelection(this.currentVariant?.options || []);
      this.setLoading(false);
    }

    renderOptions(product) {
      const container = this.querySelector('[data-quick-add-options]');
      if (!container) return;
      container.replaceChildren();

      const options = Array.isArray(product.options) ? product.options : [];
      options.forEach((option, index) => {
        const values = Array.isArray(option.values)
          ? option.values
          : [...new Set(product.variants.map((variant) => variant.options[index]).filter(Boolean))];
        const fieldset = document.createElement('fieldset');
        fieldset.className = 'quick-add-modal__fieldset';

        const legend = document.createElement('legend');
        legend.className = 'quick-add-modal__legend';
        legend.textContent = option.name || String(option);
        fieldset.append(legend);

        const valueGroup = document.createElement('div');
        valueGroup.className = 'quick-add-modal__values';
        if (values.some((value) => String(value).length > 12)) valueGroup.classList.add('quick-add-modal__values--long');

        values.forEach((value) => {
          const label = document.createElement('label');
          label.className = 'quick-add-modal__option';
          const input = document.createElement('input');
          input.type = 'radio';
          input.name = `quick-add-option-${index}`;
          input.value = value;
          input.dataset.quickAddOption = '';
          input.dataset.optionIndex = String(index);
          const text = document.createElement('span');
          text.textContent = value;
          label.append(input, text);
          valueGroup.append(label);
        });

        fieldset.append(valueGroup);
        container.append(fieldset);
      });
    }

    selectedOptions() {
      return [...this.querySelectorAll('[data-quick-add-option]:checked')]
        .sort((first, second) => Number(first.dataset.optionIndex) - Number(second.dataset.optionIndex))
        .map((input) => input.value);
    }

    handleOptionChange(input) {
      const optionIndex = Number(input.dataset.optionIndex);
      const selected = this.selectedOptions();
      selected[optionIndex] = input.value;

      let variant = this.product.variants.find((candidate) => candidate.options.every((value, index) => value === selected[index]));
      if (!variant || !variant.available) {
        variant = this.product.variants.find((candidate) => (
          candidate.available &&
          candidate.options[optionIndex] === input.value &&
          candidate.options.slice(0, optionIndex).every((value, index) => value === selected[index])
        )) || this.product.variants.find((candidate) => candidate.available && candidate.options[optionIndex] === input.value) || variant;
      }

      this.applySelection(variant?.options || selected);
    }

    applySelection(selected) {
      const variants = this.product?.variants || [];
      this.querySelectorAll('[data-quick-add-option]').forEach((input) => {
        const index = Number(input.dataset.optionIndex);
        const isSelected = selected[index] === input.value;
        const canSelect = variants.some((variant) => (
          variant.available &&
          variant.options[index] === input.value &&
          variant.options.slice(0, index).every((value, previousIndex) => value === selected[previousIndex])
        ));
        input.checked = isSelected;
        input.disabled = !canSelect && !isSelected;
        input.closest('.quick-add-modal__option')?.classList.toggle('is-selected', isSelected);
        input.closest('.quick-add-modal__option')?.classList.toggle('is-unavailable', !canSelect);
      });

      this.currentVariant = variants.find((variant) => variant.options.every((value, index) => value === selected[index])) || null;
      this.updatePurchaseArea();
    }

    updatePurchaseArea() {
      const variant = this.currentVariant;
      const variantInput = this.querySelector('[data-quick-add-variant-id]');
      const currentPrice = this.querySelector('[data-quick-add-price]');
      const comparePrice = this.querySelector('[data-quick-add-compare]');
      const button = this.querySelector('[data-quick-add-submit]');
      const buttonText = this.querySelector('[data-quick-add-submit-text]');
      const strings = this.strings();

      if (variantInput) variantInput.value = variant?.id || '';
      if (currentPrice) currentPrice.textContent = variant ? this.formatMoney(variant.price) : '';
      if (comparePrice) {
        const hasComparePrice = Boolean(variant?.compare_at_price && variant.compare_at_price > variant.price);
        comparePrice.textContent = hasComparePrice ? this.formatMoney(variant.compare_at_price) : '';
        comparePrice.hidden = !hasComparePrice;
      }
      if (button) button.disabled = !variant?.available;
      if (buttonText) buttonText.textContent = !variant ? strings.unavailable : variant.available ? strings.addToCart : strings.soldOut;

      this.updateImage(variant?.featured_image || this.product?.featured_image);
      this.showError('');
    }

    updateImage(image) {
      const element = this.querySelector('[data-quick-add-image]');
      const source = this.normalizeImage(image);
      if (!element) return;
      element.hidden = !source;
      if (source) {
        element.src = source;
        element.alt = this.product?.title || '';
      }
    }

    setBusy(isBusy) {
      const button = this.querySelector('[data-quick-add-submit]');
      this.setAttribute('aria-busy', isBusy ? 'true' : 'false');
      if (button) {
        button.disabled = isBusy || !this.currentVariant?.available;
        button.toggleAttribute('aria-busy', isBusy);
      }
      this.querySelectorAll('[data-quick-add-option], .quantity__button, .quantity__input').forEach((control) => {
        control.disabled = isBusy || (control.matches('[data-quick-add-option]') && control.closest('.is-unavailable') && !control.checked);
      });
    }

    async handleSubmit(event) {
      event.preventDefault();
      if (!this.currentVariant?.available) return;
      const form = event.currentTarget;
      const formData = new FormData(form);
      const strings = this.strings();
      this.setBusy(true);
      this.showError('');

      try {
        const response = await fetch(window.theme.routes.cartAdd, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.description || data.message || strings.addError);

        const drawer = document.querySelector('cart-drawer');
        if (window.theme.cartDrawer && drawer) {
          await drawer.refresh();
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'quick-add' } }));
          this.close();
          drawer.open();
        } else {
          window.location.href = window.theme.routes.cartUrl;
        }
      } catch (error) {
        this.showError(error.message || strings.addError);
      } finally {
        this.setBusy(false);
      }
    }

    showError(message) {
      const error = this.querySelector('[data-quick-add-error]');
      if (!error) return;
      error.textContent = message || '';
      error.hidden = !message;
    }

    getFocusableElements() {
      return [...this.querySelectorAll('button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])')]
        .filter((element) => element instanceof HTMLElement && element.offsetParent !== null);
    }

    handleKeydown(event) {
      if (!this.classList.contains('is-open')) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = this.getFocusableElements();
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  if (!customElements.get('quick-add-modal')) customElements.define('quick-add-modal', QuickAddModal);
})();
